import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Popover, PopoverContent } from '@rocco/components/popover';
import type { getPlaceDropdownOptions } from '~/modules/location/util/get-place-dropdown-options';

type PostcodeOptions = ReturnType<typeof getPlaceDropdownOptions>;

type PostcodeDropdownProps = React.ComponentProps<typeof Popover> & {
    value?: { header: string; value: { addressID?: string } };
    placeOptions?: PostcodeOptions;
    isLoading?: boolean;
    /** Index of the keyboard-highlighted option (-1 for none). */
    highlightedIndex?: number;
    onSelect?: (place: PostcodeOptions[number]) => void;
    /**
     * When false, render inside the parent instead of a body portal. Use in
     * scrollable drawers so suburb/postcode picks do not dismiss the drawer.
     */
    usePortal?: boolean;
    /**
     * `glass` (default) keeps the legacy modal's blurred surface. `white` gives
     * the gate a solid panel — over the gate's white card the blurred variant
     * has nothing to blur and reads as a grey smear.
     */
    surface?: 'glass' | 'white';
    /** Sizing for the dropdown panel (the anchor is a zero-width point). */
    contentClassName?: string;
};

/**
 * Popover dropdown for postcodes/places.
 */
export const PostcodePopover = ({
    children,
    value,
    placeOptions = [],
    isLoading = false,
    highlightedIndex = -1,
    onSelect,
    usePortal = true,
    surface = 'glass',
    contentClassName,
    ...props
}: PostcodeDropdownProps) => {
    const highlightedRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        highlightedRef.current?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    return (
        <Popover {...props}>
            {children}
            <PopoverContent
                align="start"
                background={surface === 'white' ? 'white' : 'transparent'}
                className={cn(
                    'overflow-auto rounded-lg p-0 before:rounded-lg',
                    surface === 'white'
                        ? 'max-h-[15rem] border border-[#e5e5e5] shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]'
                        : 'min-w-sm',
                    contentClassName,
                )}
                onOpenAutoFocus={(e) => e.preventDefault()}
                usePortal={usePortal}
            >
                {isLoading ? (
                    <div className="p-4" role="status" aria-live="polite">
                        Loading...
                    </div>
                ) : placeOptions.length ? (
                    <div className="flex flex-col" role="listbox">
                        {placeOptions.map((place, index) => {
                            const isHighlighted = index === highlightedIndex;
                            const isSelected =
                                place.value.addressID ===
                                value?.value.addressID;
                            return (
                                <button
                                    key={place.value.addressID}
                                    // Explicit type: default "submit" auto-submits the enquiry form when usePortal={false}.
                                    type="button"
                                    ref={
                                        isHighlighted
                                            ? highlightedRef
                                            : undefined
                                    }
                                    role="option"
                                    aria-selected={isSelected}
                                    className={cn(
                                        'align-center flex justify-between text-left',
                                        surface === 'white'
                                            ? 'px-4 py-3 text-[0.9375rem] hover:bg-black/5'
                                            : 'p-4 hover:bg-black/10',
                                        isHighlighted &&
                                            (surface === 'white'
                                                ? 'bg-black/5'
                                                : 'bg-black/10'),
                                    )}
                                    onClick={() => onSelect?.(place)}
                                >
                                    {place.header}
                                    <Check
                                        className={cn(
                                            'invisible ml-2 size-4',
                                            isSelected && 'visible',
                                        )}
                                    />
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="p-4">No results found.</div>
                )}
            </PopoverContent>
        </Popover>
    );
};
